// src/screens/s3/PhasedRetirement.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Phased Retirement / Coast Number scenario
// "What's the pot size that, from this age, simply coasts to the Freedom
// Number without any more contributions?" That's the coast number — and the
// point at which life can slow down even if the business doesn't stop.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import {
  ComposedChart, Area, Line, ReferenceLine,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// Future value: lump + monthly contribution, annual rate, years.
function fv(balance: number, monthly: number, rate: number, years: number): number {
  if (years <= 0) return balance;
  const fvLump = balance * Math.pow(1 + rate, years);
  const fvMonthly = rate === 0
    ? monthly * 12 * years
    : monthly * 12 * (Math.pow(1 + rate, years) - 1) / rate;
  return fvLump + fvMonthly;
}

export default function PhasedRetirement({ data, onUpdate }: Props) {
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const minPhased = clientAge + 1;
  const maxPhased = Math.max(freedomAge - 1, minPhased);
  const defaultPhased = Math.max(minPhased, freedomAge - 5);

  const [phasedAge, setPhasedAge] = useState<number>(
    data.s3PhasedAge && data.s3PhasedAge >= minPhased && data.s3PhasedAge <= maxPhased
      ? data.s3PhasedAge
      : defaultPhased,
  );

  const freedomNumber = data.freedomNumberInflated || 0;

  const pensionMonthly =
    (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const isaMonthly = data.planIsaMonthlyContribution || data.isaContribution || 0;

  const startPension = data.pensionBalance || 0;
  const startISA = data.investmentsBalance || 0;
  const startCash =
    (data.efAccount1Balance || 0) +
    (data.efAccount2Balance || 0) +
    (data.savingsAccounts || []).reduce(
      (s, a: { balance?: number }) => s + (a.balance || 0),
      0,
    );

  const pensionRate = data.assumptionPensionGrowth || 0.08;
  const isaRate = data.assumptionIsaGrowth || 0.08;
  const cashRate = data.assumptionCashRate || 0.01;
  const blendedRate = 0.08;

  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const yearsToPhased = Math.max(0, phasedAge - clientAge);
  const yearsCoasting = Math.max(0, yearsToFreedom - yearsToPhased);

  // Coast number: pot size at phasedAge that — with zero contributions — grows
  // to the Freedom Number by freedomAge.
  const coastNumber =
    yearsCoasting > 0
      ? freedomNumber / Math.pow(1 + blendedRate, yearsCoasting)
      : freedomNumber;

  // Full-plan projection at phasedAge (contributions kept up until phasedAge).
  const potAtPhased =
    fv(startPension, pensionMonthly, pensionRate, yearsToPhased) +
    fv(startISA, isaMonthly, isaRate, yearsToPhased) +
    fv(startCash, 0, cashRate, yearsToPhased);

  const isCoastReady = potAtPhased >= coastNumber;
  const shortfallAtPhased = Math.max(0, coastNumber - potAtPhased);

  // Chart data: three trajectories
  //  fullPlan  = contributions all the way to freedomAge
  //  coastPlan = contributions until phasedAge, then 0 — grows at blended rate
  //  floor     = pot if no further growth (flat from phasedAge)
  const chartData = useMemo(() => {
    const rows: Array<{ age: number; fullPlan: number; coastPlan: number }> = [];
    for (let age = clientAge; age <= freedomAge + 2; age++) {
      const y = age - clientAge;
      const full =
        fv(startPension, pensionMonthly, pensionRate, y) +
        fv(startISA, isaMonthly, isaRate, y) +
        fv(startCash, 0, cashRate, y);

      let coast: number;
      if (age <= phasedAge) {
        coast = full;
      } else {
        const extraYears = age - phasedAge;
        coast = potAtPhased * Math.pow(1 + blendedRate, extraYears);
      }

      rows.push({
        age,
        fullPlan: Math.round(full),
        coastPlan: Math.round(coast),
      });
    }
    return rows;
  }, [clientAge, freedomAge, phasedAge, startPension, startISA, startCash,
      pensionMonthly, isaMonthly, pensionRate, isaRate, cashRate, potAtPhased]);

  const fmt = (n: number) => `£${Math.round(n).toLocaleString()}`;
  const fmtK = (n: number) => `£${Math.round(n / 1000)}k`;

  function handleSave() {
    // Persist the coast-from age AND flip the scenario flag on so Programme
    // Summary / conditional nav pick it up.
    onUpdate({
      s3PhasedAge: phasedAge,
      s3ScenarioPhasedOn: true,
    });
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-6">
      {/* HEADER */}
      <h2 className="text-xl font-bold text-ma-navy mb-2">Phased Retirement</h2>
      <p className="text-sm text-gray-600 mb-8">
        The coast number — the pot size at a chosen age that grows to your
        Freedom Number without any more contributions. Slow down without
        stopping.
      </p>

      {/* SLIDER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Coast-from age
          </label>
          <div className="text-3xl font-bold text-ma-navy">{phasedAge}</div>
        </div>
        <input
          type="range"
          min={minPhased}
          max={maxPhased}
          step={1}
          value={phasedAge}
          onChange={(e) => setPhasedAge(Number(e.target.value))}
          className="w-full accent-ma-navy"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{minPhased}</span>
          <span>Freedom Age: {freedomAge}</span>
          <span>{maxPhased}</span>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          {yearsCoasting > 0
            ? `From age ${phasedAge}, the pot coasts for ${yearsCoasting} year${yearsCoasting === 1 ? '' : 's'} at ${(blendedRate * 100).toFixed(0)}% growth to your Freedom Number.`
            : 'Coast window is zero — choose a younger age.'}
        </p>
      </div>

      {/* STATUS / COAST NUMBER CARDS */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div
          className={`rounded-2xl border p-5 ${
            isCoastReady
              ? 'border-green-200 bg-green-50'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div
            className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
              isCoastReady ? 'text-green-800' : 'text-amber-800'
            }`}
          >
            {isCoastReady ? 'Coast-ready' : 'Not yet coast-ready'}
          </div>
          <div
            className={`text-3xl font-bold ${
              isCoastReady ? 'text-green-900' : 'text-amber-900'
            }`}
          >
            {isCoastReady ? '✓' : fmt(shortfallAtPhased)}
          </div>
          <p
            className={`text-xs mt-2 ${
              isCoastReady ? 'text-green-800' : 'text-amber-800'
            }`}
          >
            {isCoastReady
              ? `At ${phasedAge}, the pot (${fmt(potAtPhased)}) already exceeds the coast number (${fmt(coastNumber)}).`
              : `Pot at ${phasedAge} is ${fmt(potAtPhased)} vs coast number ${fmt(coastNumber)} — ${fmt(shortfallAtPhased)} short.`}
          </p>
        </div>
        <div className="rounded-2xl border border-ma-navy/20 bg-ma-navy/5 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-ma-navy mb-1">
            Coast number at age {phasedAge}
          </div>
          <div className="text-3xl font-bold text-ma-navy">{fmt(coastNumber)}</div>
          <p className="text-xs text-ma-navy/80 mt-2">
            Compounds at {(blendedRate * 100).toFixed(0)}% for {yearsCoasting} year{yearsCoasting === 1 ? '' : 's'} to
            reach the Freedom Number of {fmt(freedomNumber)}.
          </p>
        </div>
      </div>

      {/* CHART */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
          Full plan vs coast plan
        </h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="age" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} stroke="#9CA3AF" />
              <Tooltip
                formatter={(v: any) => fmt(Number(v))}
                labelFormatter={(age) => `Age ${age}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                dataKey="fullPlan"
                name="Full plan"
                fill="#1E3A5F"
                fillOpacity={0.1}
                stroke="#1E3A5F"
                strokeWidth={2}
              />
              <Line
                dataKey="coastPlan"
                name="Coast plan"
                stroke="#10B981"
                strokeWidth={2}
                dot={false}
              />
              <ReferenceLine
                y={freedomNumber}
                stroke="#C9A84C"
                strokeDasharray="4 4"
                label={{ value: 'Freedom Number', fontSize: 10, fill: '#C9A84C', position: 'insideTopLeft' }}
              />
              <ReferenceLine
                x={phasedAge}
                stroke="#10B981"
                strokeDasharray="3 3"
                label={{ value: `Coast from ${phasedAge}`, fontSize: 10, position: 'top' }}
              />
              <ReferenceLine
                x={freedomAge}
                stroke="#1E3A5F"
                strokeDasharray="3 3"
                label={{ value: `Freedom ${freedomAge}`, fontSize: 10, position: 'top' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* PLAIN ENGLISH */}
      <div className="bg-ma-navy text-white rounded-2xl p-6 mb-6">
        <p className="text-sm leading-relaxed">
          If the pot reaches <strong>{fmt(coastNumber)}</strong> by age {phasedAge},
          it will coast to your Freedom Number by {freedomAge} without another
          contribution. You're{' '}
          {isCoastReady
            ? `already there — ${fmt(potAtPhased - coastNumber)} beyond the coast number.`
            : `${fmt(shortfallAtPhased)} short right now. Every pound between here and ${phasedAge} brings the coast window closer.`}
        </p>
      </div>

      {/* VOYANT BREAK */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-800 mb-1">
          Voyant break — adviser check
        </div>
        <p className="text-sm text-blue-900 leading-relaxed">
          In Voyant, stop all contributions at age {phasedAge} and confirm the
          pot continues to reach the Freedom Number by {freedomAge} at 8% blended
          growth.
        </p>
      </div>

      {/* SAVE */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-5 py-2.5 rounded-lg bg-ma-navy text-white text-sm font-semibold hover:bg-opacity-90 transition"
        >
          Include in scenarios
        </button>
      </div>
    </div>
  );
}
